import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Activity, Clock, Star, Smile, BookOpen, Target, Gauge, X } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { useTheme, useThemeClasses } from "../hooks/useTheme";
import { useUser } from "../context/UserContext";
import { API } from "../utils/constants";
import repairAthleteSessionTimestamps from "../utils/sessionTimestampRepair";

export const SessionsPage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const theme = useThemeClasses(isDark);
  const { user } = useUser();
  
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDayForChart, setSelectedDayForChart] = useState(null);

  const fetchSessions = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      await repairAthleteSessionTimestamps(user.id);
      const res = await axios.get(`${API}/sessions?user_id=${user.id}`);
      setSessions(res.data);
    } catch (e) {
      console.error("Error fetching sessions:", e);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  // Get all rated sessions
  const filteredSessions = sessions.filter(s => s.rating);

  // Calculate summary statistics
  const getSummary = () => {
    if (filteredSessions.length === 0) return null;
    
    const totalSessions = filteredSessions.length;
    const totalDuration = filteredSessions.reduce((sum, s) => sum + (s.duration || 0), 0);
    
    // Average ratings
    const avgRatings = {
      fun: 0, learning: 0, mygoal: 0, intensity: 0
    };
    
    let ratedCount = 0;
    filteredSessions.forEach(s => {
      if (s.rating) {
        avgRatings.fun += s.rating.fun || 0;
        avgRatings.learning += s.rating.learning || 0;
        avgRatings.mygoal += s.rating.mygoal || 0;
        avgRatings.intensity += s.rating.intensity || 0;
        ratedCount++;
      }
    });
    
    if (ratedCount > 0) {
      avgRatings.fun = avgRatings.fun / ratedCount;
      avgRatings.learning = avgRatings.learning / ratedCount;
      avgRatings.mygoal = avgRatings.mygoal / ratedCount;
      avgRatings.intensity = avgRatings.intensity / ratedCount;
    }
    
    // Sessions by day
    const sessionsByDay = [0, 0, 0, 0, 0, 0, 0]; // Mon-Sun
    const ratingsByDay = [{}, {}, {}, {}, {}, {}, {}];
    
    filteredSessions.forEach(s => {
      const day = new Date(s.completed_at).getDay();
      const adjustedDay = day === 0 ? 6 : day - 1;
      sessionsByDay[adjustedDay]++;
      
      if (s.rating) {
        if (!ratingsByDay[adjustedDay].count) {
          ratingsByDay[adjustedDay] = { count: 0, fun: 0, learning: 0, mygoal: 0, intensity: 0 };
        }
        ratingsByDay[adjustedDay].count++;
        ratingsByDay[adjustedDay].fun += s.rating.fun || 0;
        ratingsByDay[adjustedDay].learning += s.rating.learning || 0;
        ratingsByDay[adjustedDay].mygoal += s.rating.mygoal || 0;
        ratingsByDay[adjustedDay].intensity += s.rating.intensity || 0;
      }
    });
    
    // Calculate averages for each day
    const ratingsByDayAvg = ratingsByDay.map(day => {
      if (!day.count) return null;
      return {
        fun: day.fun / day.count,
        learning: day.learning / day.count,
        mygoal: day.mygoal / day.count,
        intensity: day.intensity / day.count
      };
    });
    
    // Ratings by type
    const ratingsByType = {};
    filteredSessions.forEach(s => {
      const type = s.type || 'Other';
      if (!ratingsByType[type]) {
        ratingsByType[type] = { count: 0, avg_fun: 0, avg_learning: 0, avg_mygoal: 0, avg_intensity: 0 };
      }
      ratingsByType[type].count++;
      if (s.rating) {
        ratingsByType[type].avg_fun += s.rating.fun || 0;
        ratingsByType[type].avg_learning += s.rating.learning || 0;
        ratingsByType[type].avg_mygoal += s.rating.mygoal || 0;
        ratingsByType[type].avg_intensity += s.rating.intensity || 0;
      }
    });
    
    // Calculate averages
    Object.keys(ratingsByType).forEach(type => {
      const data = ratingsByType[type];
      if (data.count > 0) {
        data.avg_fun = data.avg_fun / data.count;
        data.avg_learning = data.avg_learning / data.count;
        data.avg_mygoal = data.avg_mygoal / data.count;
        data.avg_intensity = data.avg_intensity / data.count;
      }
    });
    
    return {
      total_sessions: totalSessions,
      total_duration: totalDuration,
      avg_ratings: avgRatings,
      sessions_by_day: sessionsByDay,
      ratings_by_day: ratingsByDayAvg,
      ratings_by_type: ratingsByType
    };
  };

  const summary = getSummary();

  const getDayName = (dayIndex) => {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return days[dayIndex] || '';
  };

  const sessionsByDayData = summary?.sessions_by_day?.map((count, idx) => ({
    day: getDayName(idx),
    sessions: count,
    dayIndex: idx
  })) || [];

  const getOverallAverages = () => {
    if (!summary?.avg_ratings) return { fun: 0, learning: 0, mygoal: 0, intensity: 0 };
    return summary.avg_ratings;
  };

  const getDayAverages = (dayIndex) => {
    if (!summary?.ratings_by_day || !summary.ratings_by_day[dayIndex]) return null;
    return summary.ratings_by_day[dayIndex];
  };

  const getRadarDataForDay = (dayIndex) => {
    const dayAvg = getDayAverages(dayIndex);
    const overall = getOverallAverages();
    if (!dayAvg) return null;
    return [
      { subject: 'Fun', day: dayAvg.fun || 0, overall: overall.fun || 0, fullMark: 10 },
      { subject: 'Learning', day: dayAvg.learning || 0, overall: overall.learning || 0, fullMark: 10 },
      { subject: 'MyGoal', day: dayAvg.mygoal || 0, overall: overall.mygoal || 0, fullMark: 10 },
      { subject: 'Intensity', day: dayAvg.intensity || 0, overall: overall.intensity || 0, fullMark: 10 }
    ];
  };

  const handleBarClick = (data) => {
    if (data && data.dayIndex !== undefined && summary?.ratings_by_day?.[data.dayIndex]) {
      setSelectedDayForChart(data.dayIndex);
    }
  };

  const ratingIcons = [
    { key: 'fun', label: 'Fun', icon: Smile, color: 'text-yellow-500', bgColor: isDark ? 'bg-yellow-500/20' : 'bg-yellow-100' },
    { key: 'learning', label: 'Learn', icon: BookOpen, color: 'text-blue-500', bgColor: isDark ? 'bg-blue-500/20' : 'bg-blue-100' },
    { key: 'mygoal', label: 'Goal', icon: Target, color: 'text-purple-500', bgColor: isDark ? 'bg-purple-500/20' : 'bg-purple-100' },
    { key: 'intensity', label: 'Intensity', icon: Gauge, color: 'text-red-500', bgColor: isDark ? 'bg-red-500/20' : 'bg-red-100' }
  ];

  return (
    <div className={`min-h-screen ${theme.bg} flex flex-col transition-colors duration-300`} data-testid="page-sessions">
      <header className="flex items-center justify-between p-4 pt-6">
        <button data-testid="back-button" onClick={() => navigate("/")} className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${theme.button}`}>
          <ArrowLeft className={`w-5 h-5 ${theme.textPrimary}`} />
        </button>
        <h1 className={`text-xl font-bold ${theme.textPrimary}`}>My Sessions</h1>
        <div className="w-10" /> {/* Spacer for alignment */}
      </header>

      <div className="flex-1 px-4 pb-8 overflow-y-auto">
        {loading ? (
          <div className={`text-center py-8 ${theme.textMuted}`}>Loading...</div>
        ) : (
          <>
            {/* Summary Cards */}
            {summary && (
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Activity className={`w-4 h-4 ${theme.textMuted}`} />
                    <span className={`text-xs ${theme.textMuted}`}>Rated Sessions</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <p className={`text-2xl font-bold ${theme.textPrimary}`}>{summary.total_sessions}</p>
                    {summary.avg_ratings && (
                      <span className={`text-xs ${theme.textMuted}`}>
                        ⭐ {((summary.avg_ratings.fun + summary.avg_ratings.learning + summary.avg_ratings.mygoal + summary.avg_ratings.intensity) / 4).toFixed(1)} avg
                      </span>
                    )}
                  </div>
                </div>
                <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className={`w-4 h-4 ${theme.textMuted}`} />
                    <span className={`text-xs ${theme.textMuted}`}>Total Time</span>
                  </div>
                  <p className={`text-2xl font-bold ${theme.textPrimary}`}>
                    {summary.total_duration} <span className="text-sm font-normal">min</span>
                  </p>
                </div>
              </div>
            )}

            {/* Ratings by Type */}
            {summary?.ratings_by_type && Object.keys(summary.ratings_by_type).length > 0 && (
              <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl mb-4`}>
                <h3 className={`font-semibold mb-3 ${theme.textPrimary}`}>Ratings by Training Type</h3>
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {Object.entries(summary.ratings_by_type).map(([type, data]) => (
                    <div key={type} className={`flex-shrink-0 w-40 p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-[#493986]/5'}`}>
                      <p className={`text-sm font-medium mb-2 ${theme.textPrimary}`}>{type}</p>
                      <p className={`text-xs mb-2 ${theme.textMuted}`}>
                        {data.count} session{data.count !== 1 ? 's' : ''} • ⭐ {((data.avg_fun + data.avg_learning + data.avg_mygoal + data.avg_intensity) / 4).toFixed(1)} avg
                      </p>
                      <div className="space-y-1.5">
                        {ratingIcons.map(r => (
                          <div key={r.key} className="flex items-center gap-2">
                            <r.icon className={`w-3 h-3 ${r.color}`} />
                            <div className={`flex-1 h-2 rounded-full ${isDark ? 'bg-white/10' : 'bg-[#493986]/10'}`}>
                              <div className={`h-full rounded-full ${r.bgColor}`} style={{ width: `${(data[`avg_${r.key}`] || 0) * 10}%` }} />
                            </div>
                            <span className={`text-xs ${theme.textMuted} w-6`}>{(data[`avg_${r.key}`] || 0).toFixed(1)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sessions by Day Chart */}
            {sessionsByDayData.length > 0 && sessionsByDayData.some(d => d.sessions > 0) && (
              <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl mb-4`}>
                <h3 className={`font-semibold mb-3 ${theme.textPrimary}`}>Sessions by Day</h3>
                <p className={`text-xs mb-3 ${theme.textMuted}`}>Tap a bar to see day ratings</p>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={sessionsByDayData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.1)' : 'rgba(73,57,134,0.1)'} />
                    <XAxis dataKey="day" stroke={isDark ? 'rgba(255,255,255,0.5)' : 'rgba(73,57,134,0.5)'} fontSize={12} />
                    <YAxis stroke={isDark ? 'rgba(255,255,255,0.5)' : 'rgba(73,57,134,0.5)'} fontSize={10} />
                    <Tooltip contentStyle={{ backgroundColor: isDark ? '#1a1a2e' : '#fff', border: 'none', borderRadius: '8px' }} />
                    <Bar dataKey="sessions" fill="url(#colorGradientPlayer)" radius={[4, 4, 0, 0]} cursor="pointer" onClick={handleBarClick} />
                    <defs>
                      <linearGradient id="colorGradientPlayer" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#493986" />
                        <stop offset="100%" stopColor="#92a7e8" />
                      </linearGradient>
                    </defs>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Sessions List */}
            {filteredSessions.length > 0 && (
              <div className={`p-4 rounded-2xl ${theme.card} border backdrop-blur-xl`}>
                <h3 className={`font-semibold mb-3 ${theme.textPrimary}`}>My Rated Sessions</h3>
                <div className="space-y-3">
                  {filteredSessions.slice(0, 15).map((session) => (
                    <div key={session.id} className={`p-3 rounded-xl ${isDark ? 'bg-white/5' : 'bg-[#493986]/5'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#493986]/20 to-[#92a7e8]/20 flex items-center justify-center">
                            <Activity className={`w-5 h-5 ${theme.textPrimary}`} />
                          </div>
                          <div>
                            <p className={`font-medium ${theme.textPrimary}`}>{session.name}</p>
                            <p className={`text-xs ${theme.textMuted}`}>{session.type} • {new Date(session.completed_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                        {session.rating && (
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                            <span className={`text-sm font-medium ${theme.textPrimary}`}>
                              {((session.rating.fun + session.rating.learning + session.rating.mygoal + session.rating.intensity) / 4).toFixed(1)}
                            </span>
                          </div>
                        )}
                      </div>
                      {session.rating && (
                        <div className="flex gap-2 flex-wrap">
                          <span className={`text-xs px-2 py-1 rounded-lg ${isDark ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-600'}`}>
                            Fun: {session.rating.fun}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded-lg ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'}`}>
                            Learn: {session.rating.learning}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded-lg ${isDark ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-600'}`}>
                            Goal: {session.rating.mygoal}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded-lg ${isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600'}`}>
                            Int: {session.rating.intensity}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {filteredSessions.length === 0 && !loading && (
              <div className={`text-center py-12`}>
                <Activity className={`w-16 h-16 mx-auto mb-4 ${theme.textMuted}`} />
                <h3 className={`font-semibold mb-2 ${theme.textPrimary}`}>No rated sessions found</h3>
                <p className={`text-sm ${theme.textMuted}`}>
                  {sessions.length > 0 
                    ? 'Try adjusting your filters or rate some sessions in the Agenda' 
                    : 'Start by rating your training sessions in the Agenda'}
                </p>
                <button 
                  onClick={() => navigate('/agenda')}
                  className="mt-4 px-6 py-2 bg-gradient-to-r from-[#493986] to-[#92a7e8] text-white font-medium rounded-xl"
                >
                  Go to Agenda
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Day Radar Chart Modal */}
      {selectedDayForChart !== null && getRadarDataForDay(selectedDayForChart) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSelectedDayForChart(null)} />
          <div className={`relative w-full max-w-md rounded-2xl ${isDark ? 'bg-[#0a0a0f]' : 'bg-white'} p-4`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`font-semibold ${theme.textPrimary}`}>{getDayName(selectedDayForChart)} Ratings</h3>
              <button onClick={() => setSelectedDayForChart(null)} className={`w-8 h-8 rounded-full ${theme.button} flex items-center justify-center`}>
                <X className={`w-4 h-4 ${theme.textMuted}`} />
              </button>
            </div>
            <div className="flex justify-center gap-4 mb-2 text-xs">
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-[#493986]" />
                <span className={theme.textMuted}>{getDayName(selectedDayForChart)}</span>
              </span>
              <span className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <span className={theme.textMuted}>Overall Avg</span>
              </span>
            </div>
            <ResponsiveContainer width="100%" height={250}>
              <RadarChart data={getRadarDataForDay(selectedDayForChart)}>
                <PolarGrid stroke={isDark ? 'rgba(255,255,255,0.1)' : 'rgba(73,57,134,0.1)'} />
                <PolarAngleAxis dataKey="subject" tick={{ fill: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(73,57,134,0.6)', fontSize: 12 }} />
                <PolarRadiusAxis domain={[0, 10]} tick={{ fill: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(73,57,134,0.4)', fontSize: 10 }} />
                <Radar name="Overall" dataKey="overall" stroke="#eab308" fill="#eab308" fillOpacity={0.2} />
                <Radar name="Day" dataKey="day" stroke="#493986" fill="#493986" fillOpacity={0.5} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionsPage;
